import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const KEYWORD_CATEGORY_OPTIONS = [
  "核心服務",
  "治療相關",
  "美學相關",
  "問題導向",
  "地區性",
  "品牌相關",
  "競品比較",
  "長尾關鍵字",
  "未分類",
] as const;

interface KeywordCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultKeyword?: string;
  defaultCategory?: string | null;
  onCreated?: (created: { keyword: string; category: string }) => void;
}

export default function KeywordCreateDialog({
  open,
  onOpenChange,
  defaultKeyword = "",
  defaultCategory = "未分類",
  onCreated,
}: KeywordCreateDialogProps) {
  const [keyword, setKeyword] = useState(defaultKeyword);
  const [category, setCategory] = useState(
    KEYWORD_CATEGORY_OPTIONS.includes((defaultCategory || "未分類") as (typeof KEYWORD_CATEGORY_OPTIONS)[number])
      ? (defaultCategory || "未分類")
      : "未分類"
  );
  const utils = trpc.useUtils();

  useEffect(() => {
    if (!open) return;
    setKeyword(defaultKeyword);
    setCategory(
      KEYWORD_CATEGORY_OPTIONS.includes((defaultCategory || "未分類") as (typeof KEYWORD_CATEGORY_OPTIONS)[number])
        ? (defaultCategory || "未分類")
        : "未分類"
    );
  }, [defaultCategory, defaultKeyword, open]);

  const createMutation = trpc.keywords.create.useMutation({
    onSuccess: () => {
      utils.keywords.getAll.invalidate();
      utils.keywords.getCategories.invalidate();
      toast.success("關鍵字已加入列表");
      onCreated?.({
        keyword: keyword.trim(),
        category,
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(`加入失敗：${error.message}`);
    },
  });

  const handleCreate = () => {
    if (!keyword.trim()) {
      toast.error("請輸入關鍵字");
      return;
    }

    createMutation.mutate({
      keyword: keyword.trim(),
      category,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>新增關鍵字</DialogTitle>
          <DialogDescription>
            新增要追蹤的關鍵字，系統將自動抓取趨勢數據
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="create-keyword">關鍵字 *</Label>
            <Input
              id="create-keyword"
              placeholder="例如：植牙推薦"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-category">分類</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="create-category">
                <SelectValue placeholder="選擇分類" />
              </SelectTrigger>
              <SelectContent>
                {KEYWORD_CATEGORY_OPTIONS.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? "新增中..." : "新增"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
